import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { updateOne, useBufferValueAction } from '../../containers/App/actions';
import { VENUES } from '../../constants/endpointConstants';
import VenueModal from '../../containers/VenueModal';
import EditVenueCard from '../EditVenueCard';
import ActionStatusModal from '../ActionStatusModal';
import { useTranslation } from '../../src/i18n';
import TextButton from '../TextButton';
import Button from '../Button';
import { TP } from '../../constants/consts';
import { selectBufferValueData } from '../../containers/App/selectors';
import FbCircularProgress from '../FbCircularProgress';
import EditFormFooter from './EditFormFooter';
import { EditLayoutHeader } from './EditLayout/EditLayoutHeader';
import NoListItems from '../NoListItems';

const useStyles = makeStyles(theme => ({
  card: {
    marginTop: theme.spacing(2),
  },
}));

const EditVenues = ({ venuesHost, goToNextTab, getVenues, getEntity, updateVenue, venuesData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [editModalOpened, setEditModalOpened] = React.useState(false);
  const [venueToRemove, setVenueToRemove] = React.useState(null);

  const processingData = useSelector(selectBufferValueData());

  const [venueToEdit, setVenueToEdit] = React.useState(undefined);
  const entityId = get(venuesHost, 'id');
  const { hostName } = venuesHost;

  React.useEffect(() => {
    getVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const onCloseVenueModal = () => {
    setEditModalOpened(false);
    setVenueToEdit(undefined);
  };

  const editVenueCallback = () => {
    getVenues(onCloseVenueModal);
  };

  const updateDefaultVenue = venue => {
    dispatch(
      updateOne({
        endpoint: VENUES,
        id: venue?.id,
        sagaRoutine: useBufferValueAction,
        params: venue,
        callback: getVenues,
      }),
    );
  };

  const updateVenueDefaultField = venue => ({
    id: venue?.id,
    [hostName]: venue[hostName]?.map(c => (c.id === venuesHost.id ? { ...c, isDefault: venue.isDefault } : c)),
  });

  const markAsDefault = (isChecked, venue) => {
    const previousDefaultVenue = venuesData?.data?.find(v => v?.isDefault);
    if (isChecked) {
      if (!previousDefaultVenue) {
        updateDefaultVenue(updateVenueDefaultField({ ...venue, isDefault: true }));
      } else {
        updateVenue(
          [
            updateVenueDefaultField({ ...venue, isDefault: true }),
            updateVenueDefaultField({ ...previousDefaultVenue, isDefault: false }),
          ],
          getVenues,
        );
      }
    } else {
      updateDefaultVenue(updateVenueDefaultField({ ...venue, isDefault: false }));
    }
  };

  const removeVenue = () => {
    dispatch(
      updateOne({
        endpoint: VENUES,
        id: venueToRemove?.id,
        sagaRoutine: useBufferValueAction,
        params: {
          [hostName]: venueToRemove[hostName]?.filter(c => c?.id !== venuesHost?.id),
        },
        callback: () => {
          setVenueToRemove(null);
          getVenues();
          getEntity();
        },
        successMessage: `Successfully deleted ${venueToRemove?.name} venue`,
        errorMessage: `Error deleted ${venueToRemove?.name} venue`,
      }),
    );
  };

  return (
    <>
      <EditLayoutHeader
        title={`${TP}.m_VENUES`}
        subtitle={`${TP}.FN_VENUES_SUBTITLE`}
        actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setEditModalOpened(true);
              setVenueToEdit({ [hostName]: [{ id: entityId }] });
            }}
          >
            {t(`${TP}.ADD_VENUE`)}
          </Button>
        }
      />
      {venuesData?.loading ? (
        <FbCircularProgress />
      ) : (
        venuesData?.data?.map(venue => (
          <EditVenueCard
            key={`edit-venue-${get(venue, 'id')}`}
            onEditClick={() => {
              setVenueToEdit(venue);
              setEditModalOpened(!editModalOpened);
            }}
            onDelete={setVenueToRemove}
            onDefaultCheck={isChecked => markAsDefault(isChecked, venue)}
            venue={venue}
            className={classes.card}
          />
        ))
      )}
      {!venuesData.loading && !venuesData?.data?.length && !venuesData.error && venuesData?.loadedOnce && (
        <NoListItems text={t(`${TP}.FN_NO_VENUES_ADDED`)} />
      )}
      <EditFormFooter submitFunc={goToNextTab} lastUpdatedDate={venuesHost?.updatedAt} />
      {editModalOpened && (
        <VenueModal
          incomingData={venueToEdit}
          onClose={onCloseVenueModal}
          callback={editVenueCallback}
          isSearchable={!venueToEdit.id}
          hostName={hostName}
          entityVenues={venuesData?.data || []}
        />
      )}
      {venueToRemove && (
        <ActionStatusModal
          isOpened
          onClose={() => setVenueToRemove(null)}
          type="warning"
          title={t(`${TP}.FN_REMOVE_VENUE_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={() => setVenueToRemove(null)} key="cancel-button">
              {t(`${TP}.m_CANCEL`)}
            </TextButton>,
            <Button onClick={removeVenue} key="approve-button" isLoading={processingData?.loading}>
              {t(`${TP}.m_DELETE`)}
            </Button>,
          ]}
        />
      )}
    </>
  );
};

EditVenues.propTypes = {
  venuesHost: PropTypes.object.isRequired,
  goToNextTab: PropTypes.func.isRequired,
  getVenues: PropTypes.func.isRequired,
  getEntity: PropTypes.func.isRequired,
  updateVenue: PropTypes.func.isRequired,
  venuesData: PropTypes.object.isRequired,
};

export default EditVenues;
